import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { AxiosError } from 'axios';

import { showSuccessToast, showWarningToast } from '@/components/ui/sonner';
import {
  type SaleApplicationData,
  type SaleApplicationStepOneData,
  type SaleApplicationStepTwoData,
  SALE_APPLICATION_DEFAULT_VALUES,
  SALE_APPLICATION_STEP_ONE_FIELD_KEYS,
  SALE_APPLICATION_STEP_TWO_FIELD_KEYS,
  saleApplicationStepOneSchema,
  saleApplicationStepTwoSchema,
} from '@/features/original-sale';
import { submitSaleApplicationReq } from '@/features/original-sale/api/sale.requests';
import {
  invalidateSellerProfileCache,
  isSellerApplicationStageTwoPending,
} from '@/features/original-sale/hooks/use-seller-profile-query';
import type { SellerProfileResponse } from '@/features/original-sale/types/sale.types';
import {
  convertIsoDateToSaleDateMask,
  convertSaleDateMaskToIso,
} from '@/features/original-sale/utils/sale-date-mask.utils';

const SALE_FORM_STEP_ONE = 1;
const SALE_FORM_STEP_TWO = 2;

const getInitialFormStep = (profile?: SellerProfileResponse | null): number =>
  isSellerApplicationStageTwoPending(profile) ? SALE_FORM_STEP_TWO : SALE_FORM_STEP_ONE;

const mapProfileToFormValues = (profile: SellerProfileResponse): Partial<SaleApplicationData> => ({
  name: profile.passport?.firstName ?? '',
  phone: profile.phone ?? '',
  contactLink: profile.contactSocialUrl ?? '',
  profileLink: profile.externalProfileUrl ?? '',
  entityType: profile.applicantType ?? 'individual',
  citizenship: profile.citizenshipCode ?? 'RU',
  lastName: profile.passport?.lastName ?? '',
  firstName: profile.passport?.firstName ?? '',
  middleName: profile.passport?.middleName ?? '',
  passportIssuedBy: profile.passport?.issuedBy ?? '',
  passportIssuedDate: profile.passport?.issueDate
    ? convertIsoDateToSaleDateMask(profile.passport.issueDate)
    : '',
  birthDate: profile.passport?.birthDate
    ? convertIsoDateToSaleDateMask(profile.passport.birthDate)
    : '',
  bankName: profile.payout?.bankName ?? '',
  recipientFullName: profile.payout?.recipientFullName ?? '',
  bik: profile.payout?.bik ?? '',
  recipientInn: profile.payout?.recipientInn ?? '',
  consent: Boolean(profile.termsAcceptedAt),
});

const getSaleApplicationErrorMessage = (error: unknown): string => {
  let errorMessage = 'Не удалось отправить заявление.';

  if (error instanceof AxiosError && error.response?.data?.message) {
    errorMessage += ` ${error.response.data.message}`;
  } else if (error instanceof Error && error.message) {
    errorMessage += ` ${error.message}`;
  }

  return errorMessage;
};

interface UseSaleApplicationFormOptions {
  fanficId?: string;
  onSuccess?: () => void;
  profile?: SellerProfileResponse | null;
}

export const useSaleApplicationForm = ({
  fanficId,
  onSuccess,
  profile,
}: UseSaleApplicationFormOptions = {}) => {
  const [currentStep, setCurrentStep] = useState(() => getInitialFormStep(profile));
  const currentStepRef = useRef(currentStep);

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const form = useForm<SaleApplicationData>({
    resolver: async (values, context, options) => {
      const schema =
        currentStepRef.current === SALE_FORM_STEP_ONE
          ? saleApplicationStepOneSchema
          : saleApplicationStepTwoSchema;

      return zodResolver(schema)(values, context, options);
    },
    mode: 'onChange',
    defaultValues: {
      ...SALE_APPLICATION_DEFAULT_VALUES,
      ...(profile ? mapProfileToFormValues(profile) : {}),
    },
  });

  useEffect(() => {
    form.clearErrors();
  }, [currentStep, form]);

  const activeStepFieldKeys =
    currentStep === SALE_FORM_STEP_ONE
      ? SALE_APPLICATION_STEP_ONE_FIELD_KEYS
      : SALE_APPLICATION_STEP_TWO_FIELD_KEYS;

  useFormState({
    control: form.control,
    name: [...activeStepFieldKeys],
  });

  const { isSubmitting } = useFormState({ control: form.control });

  const isValid =
    currentStep === SALE_FORM_STEP_ONE
      ? saleApplicationStepOneSchema.safeParse(form.getValues()).success
      : saleApplicationStepTwoSchema.safeParse(form.getValues()).success;

  const submitStepOne = useCallback(async (data: SaleApplicationStepOneData) => {
    try {
      await submitSaleApplicationReq({
        applicationStage: 1,
        phone: data.phone,
        contactSocialUrl: data.contactLink,
        externalProfileUrl: data.profileLink || undefined,
        acceptTerms: data.consent,
        passport: {
          firstName: data.name,
        },
      });

      invalidateSellerProfileCache();
      showSuccessToast('Данные первого этапа успешно отправлены');
      setCurrentStep(SALE_FORM_STEP_TWO);
    } catch (error) {
      console.error('Sale application step one submission error:', error);
      showWarningToast(getSaleApplicationErrorMessage(error));
    }
  }, []);

  const submitStepTwo = useCallback(
    async (data: SaleApplicationStepTwoData) => {
      try {
        await submitSaleApplicationReq({
          applicationStage: 2,
          acceptTerms: true,
          applicantType: data.entityType,
          citizenshipCode: data.citizenship,
          passport: {
            lastName: data.lastName,
            firstName: data.firstName,
            middleName: data.middleName,
            number: data.passportNumber,
            issuedBy: data.passportIssuedBy,
            issueDate: convertSaleDateMaskToIso(data.passportIssuedDate),
            birthDate: convertSaleDateMaskToIso(data.birthDate),
          },
          payout: {
            bankName: data.bankName,
            accountNumber: data.bankAccount,
            bik: data.bik,
            recipientFullName: data.recipientFullName,
            recipientInn: data.recipientInn,
          },
        });

        invalidateSellerProfileCache();
        showSuccessToast('Заявление успешно отправлено');
        form.reset(SALE_APPLICATION_DEFAULT_VALUES);
        setCurrentStep(SALE_FORM_STEP_ONE);
        onSuccess?.();
      } catch (error) {
        console.error('Sale application step two submission error:', error);
        showWarningToast(getSaleApplicationErrorMessage(error));
      }
    },
    [form, onSuccess],
  );

  const onSubmit = useCallback(
    async (data: SaleApplicationData) => {
      if (currentStep === SALE_FORM_STEP_ONE) {
        await submitStepOne(saleApplicationStepOneSchema.parse(data));
        return;
      }

      await submitStepTwo(saleApplicationStepTwoSchema.parse(data));
    },
    [currentStep, submitStepOne, submitStepTwo],
  );

  return {
    form,
    currentStep,
    onSubmit: form.handleSubmit(onSubmit),
    isSubmitting,
    isValid,
    fanficId,
  };
};
